import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import StuffedAnimalList from '../components/StuffedAnimalList';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = 'https://puffies-b24ce.ue.r.appspot.com/api';

// Utility function to check if it's an animal's birthday
const isBirthday = (birthday) => {
  if (!birthday || birthday === 'Unknown') return false;
  
  const today = new Date();
  const [month, day] = birthday.split(' ');
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  return months[today.getMonth()] === month && today.getDate().toString() === day;
};

// Function to convert birthday string to a comparable date value
const getBirthdayValue = (birthday) => {
  if (!birthday || birthday === 'Unknown') return new Date(9999, 11, 31); // Put unknown/empty dates at the end
  
  const [month, day] = birthday.split(' ');
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthIndex = months.indexOf(month);
  
  // Use current year for consistent comparison
  const currentYear = new Date().getFullYear();
  return new Date(currentYear, monthIndex, parseInt(day));
};

// Function to map status to display text
const getDisplayStatus = (status) => {
  switch (status) {
    case 'Purchased':
      return 'Available';
    case 'Not Purchased':
      return 'Not Available';
    case 'Lucy':
      return 'Owned';
    default:
      return status;
  }
};

// Styled components for consistent card layout
const StyledCard = styled(Card)(({ theme, wanted, status }) => {
  let borderColor;
  
  if (!wanted) {
    borderColor = theme.palette.cardBorders.notWanted;
  } else {
    // WANTED items
    switch (status) {
      case 'Lucy':
        borderColor = theme.palette.cardBorders.wantedLucy;
        break;
      case 'Purchased':
        borderColor = theme.palette.cardBorders.wantedPurchased;
        break;
      case 'Not Purchased':
        borderColor = theme.palette.cardBorders.wantedNotPurchased;
        break;
      default:
        borderColor = theme.palette.cardBorders.notWanted;
    }
  }

  return {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderColor: borderColor,
  };
});

const StyledCardContent = styled(CardContent)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const DescriptionBox = styled(Box)({
  flexGrow: 1,
  minHeight: '80px',
  maxHeight: '80px',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
});

const MetadataBox = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

// Add styled component for birthday display
const BirthdayBox = styled(Box)(({ theme, isbirthday }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: isbirthday === 'true' ? theme.palette.success.main : theme.palette.text.secondary,
  fontWeight: isbirthday === 'true' ? 'bold' : 'normal',
}));

const Home = () => {
  const [stuffedAnimals, setStuffedAnimals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailedError, setDetailedError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  // Initialize state from localStorage or use defaults
  const [statusFilter, setStatusFilter] = useState(() => 
    localStorage.getItem('puffies_statusFilter') || 'all'
  );
  const [wantedFilter, setWantedFilter] = useState(() => 
    localStorage.getItem('puffies_wantedFilter') || 'all'
  );
  const [sortBy, setSortBy] = useState(() => 
    localStorage.getItem('puffies_sortBy') || 'status'
  );
  const [viewMode, setViewMode] = useState(() => 
    localStorage.getItem('puffies_viewMode') || 'grid'
  );

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('puffies_statusFilter', statusFilter);
    localStorage.setItem('puffies_wantedFilter', wantedFilter);
    localStorage.setItem('puffies_sortBy', sortBy);
    localStorage.setItem('puffies_viewMode', viewMode);
  }, [statusFilter, wantedFilter, sortBy, viewMode]);

  // Active filters count
  const activeFiltersCount = [
    statusFilter !== 'all',
    wantedFilter !== 'all',
  ].filter(Boolean).length;

  const [loadingToggles, setLoadingToggles] = useState(new Set());
  const [imageSearchLoading, setImageSearchLoading] = useState(false);

  useEffect(() => {
    fetchStuffedAnimals();
  }, []);

  const fetchStuffedAnimals = async () => {
    try {
      setLoading(true);
      setError('');
      setDetailedError(null);
      const response = await axios.get(`${API_BASE_URL}/stuffed-animals`);
      setStuffedAnimals(response.data);
    } catch (error) {
      const errorMessage = 'Error fetching stuffed animals: ' + error.message;
      setError(errorMessage);
      setDetailedError({
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: `${API_BASE_URL}/stuffed-animals`,
        details: error.response?.data || error.stack
      });
      console.error('Error fetching stuffed animals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTextSearch = async () => {
    if (!searchQuery.trim()) {
      return fetchStuffedAnimals();
    }

    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/stuffed-animals/search/${searchQuery}`);
      setStuffedAnimals(response.data);
      if (response.data.length === 0) {
        setError(`No stuffed animals found matching "${searchQuery}"`);
      }
    } catch (error) {
      setError('Error searching stuffed animals: ' + error.message);
      console.error('Error searching stuffed animals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSearch = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      setImageSearchLoading(true);
      setError('');
      const response = await axios.post(`${API_BASE_URL}/api/image-search/search`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.matches.length === 0) {
        setError(`No matching stuffed animals found. Labels detected: ${response.data.labels.join(', ')}`);
      } else {
        setStuffedAnimals(response.data.matches);
      }
    } catch (error) {
      setError('Error searching by image: ' + (error.response?.data?.message || error.message));
      console.error('Error searching by image:', error);
    } finally {
      setImageSearchLoading(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) {
      return 'https://via.placeholder.com/200?text=No+Image';
    }
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    // Convert all paths to Cloud Storage URLs
    const filename = imageUrl.split('/').pop();
    return `https://storage.googleapis.com/puffies-images/${filename}`;
  };

  const toggleWanted = async (id) => {
    if (loadingToggles.has(id)) return;
    
    try {
      setLoadingToggles(prev => new Set([...prev, id]));
      setError('');
      
      const response = await axios.patch(`${API_BASE_URL}/stuffed-animals/${id}/toggle-wanted`);
      
      if (response.data) {
        // Update the UI with the new data
        setStuffedAnimals(prevAnimals =>
          prevAnimals.map(animal =>
            animal._id === id ? response.data : animal
          )
        );
      } else {
        throw new Error('No data received from server');
      }
    } catch (error) {
      console.error('Toggle failed:', error);
      // Get the specific error message from the response if available
      const errorMessage = error.response?.data?.message || error.message;
      setError(errorMessage);
      setSnackbarOpen(true);
      
      // Revert the UI state for this specific item
      setStuffedAnimals(prevAnimals =>
        prevAnimals.map(animal =>
          animal._id === id ? { ...animal, wanted: !animal.wanted } : animal
        )
      );
    } finally {
      setLoadingToggles(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Filter and sort animals
  const getFilteredAndSortedAnimals = () => {
    // First filter
    const filtered = stuffedAnimals.filter(animal => {
      if (statusFilter !== 'all' && animal.status !== statusFilter) return false;
      if (wantedFilter !== 'all') {
        if (wantedFilter === 'true' && !animal.wanted) return false;
        if (wantedFilter === 'false' && animal.wanted) return false;
      }
      return true;
    });

    // Then sort
    const sorted = [...filtered];
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'birthday':
        return sorted.sort((a, b) => {
          const dateA = getBirthdayValue(a.birthday);
          const dateB = getBirthdayValue(b.birthday);
          return dateA - dateB;
        });
      case 'status':
      default:
        return sorted.sort((a, b) => a.status.localeCompare(b.status));
    }
  };

  return (
    <Container maxWidth="lg" sx={{ px: 4 }}>
      {isAdmin && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            sx={{ 
              bgcolor: '#1b5e20',
              '&:hover': {
                bgcolor: '#0d3311',
              },
              py: 1.5,
              px: 3,
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
            startIcon={<AddIcon />}
            onClick={() => navigate('/edit')}
          >
            Add Stuffie
          </Button>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTextSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleTextSearch}
                disabled={loading}
                sx={{ bgcolor: '#8B0000', '&:hover': { bgcolor: '#6B0000' } }}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Not Purchased">Not Available</MenuItem>
                <MenuItem value="Purchased">Available</MenuItem>
                <MenuItem value="Lucy">Owned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Wanted</InputLabel>
              <Select
                value={wantedFilter}
                onChange={(e) => setWantedFilter(e.target.value)}
                label="Wanted"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Wanted</MenuItem>
                <MenuItem value="false">Not Wanted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="birthday">Birthday</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {activeFiltersCount > 0 && (
            <Grid item>
              <Button
                onClick={() => {
                  setStatusFilter('all');
                  setWantedFilter('all');
                  setSortBy('status');
                  setViewMode('grid');
                  // Clear localStorage when filters are cleared
                  localStorage.removeItem('puffies_statusFilter');
                  localStorage.removeItem('puffies_wantedFilter');
                  localStorage.removeItem('puffies_sortBy');
                  localStorage.removeItem('puffies_viewMode');
                }}
                sx={{ color: '#8B0000' }}
              >
                Clear Filters ({activeFiltersCount})
              </Button>
            </Grid>
          )}
          <Grid item sx={{ ml: 'auto' }}>
            <IconButton
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              sx={{ color: '#8B0000' }}
            >
              {viewMode === 'grid' ? <ListIcon /> : <GridViewIcon />}
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          {isAdmin && detailedError && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Debug Information (Admin Only):</Typography>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85em' }}>
                {JSON.stringify({
                  url: detailedError.url,
                  status: detailedError.status,
                  statusText: detailedError.statusText,
                  message: detailedError.message,
                  details: detailedError.details
                }, null, 2)}
              </pre>
            </Alert>
          )}
        </>
      )}

      {viewMode === 'list' ? (
        <StuffedAnimalList
          stuffedAnimals={getFilteredAndSortedAnimals()}
          onToggleWanted={toggleWanted}
          loadingToggles={loadingToggles}
          isAdmin={isAdmin}
          onEdit={(id) => navigate(`/edit/${id}`)}
          getImageUrl={getImageUrl}
        />
      ) : (
        <Grid container spacing={3}>
          {getFilteredAndSortedAnimals().map((animal) => (
            <Grid item key={animal._id} xs={12} sm={6} md={4}>
              <StyledCard
                variant="outlined"
                wanted={animal.wanted}
                status={animal.status}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={getImageUrl(animal.imageUrl)}
                  alt={animal.name}
                  sx={{ objectFit: 'contain', bgcolor: 'background.paper' }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="div" gutterBottom>
                        {animal.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                          label={getDisplayStatus(animal.status)}
                          size="small"
                          color={
                            animal.status === 'Lucy'
                              ? 'error'
                              : animal.status === 'Purchased'
                              ? 'info'
                              : 'default'
                          }
                          sx={animal.status === 'Lucy' ? { bgcolor: '#8B0000', color: 'white' } : {}}
                        />
                        {animal.wanted && (
                          <Chip label="Wanted" size="small" color="primary" />
                        )}
                        {animal.birthday && (
                          <Chip
                            label={`${animal.birthday}${isBirthday(animal.birthday) ? ' 🎂' : ''}`}
                            size="small"
                            color={isBirthday(animal.birthday) ? 'success' : 'default'}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {animal.description}
                      </Typography>
                    </Box>
                    <Box>
                      {isAdmin ? (
                        <>
                          <IconButton
                            onClick={() => toggleWanted(animal._id)}
                            disabled={loadingToggles.has(animal._id)}
                            sx={{ color: '#8B0000' }}
                          >
                            {animal.wanted ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                          </IconButton>
                          <IconButton
                            onClick={() => navigate(`/edit/${animal._id}`)}
                            sx={{ color: '#8B0000' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </>
                      ) : (
                        animal.wanted ? (
                          <FavoriteIcon color="primary" />
                        ) : (
                          <FavoriteBorderIcon color="disabled" />
                        )
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="Wanted status updated"
      />
    </Container>
  );
};

export default Home; 